"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { CreditCard, Tv, Zap } from "lucide-react"
import Card from "../components/Card"
import Input from "../components/Input"
import Button from "../components/Button"
import Modal from "../components/Modal"
import Loader from "../components/Loader"
import { getCablePlans, payCableBill, payElectricityBill } from "../utils/api"
import toast from "react-hot-toast"

const PayBills = () => {
  const { user } = useAuth()
  const { addTransaction, deductBalance } = useWallet()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState("cable")
  const [selectedProvider, setSelectedProvider] = useState("")
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [formData, setFormData] = useState({
    smartCardNumber: "",
    meterNumber: "",
    customerName: "",
    phone: "",
    amount: "",
  })
  const [errors, setErrors] = useState({})

  const cableProviders = [
    { id: "dstv", name: "DStv", logo: "📺", color: "bg-blue-600" },
    { id: "gotv", name: "GOtv", logo: "📻", color: "bg-green-600" },
    { id: "startimes", name: "StarTimes", logo: "⭐", color: "bg-purple-600" },
  ]

  const electricityProviders = [
    { id: "aedc", name: "AEDC", logo: "⚡", color: "bg-yellow-600" },
    { id: "ekedc", name: "EKEDC", logo: "⚡", color: "bg-red-600" },
    { id: "ikedc", name: "IKEDC", logo: "⚡", color: "bg-blue-600" },
    { id: "phed", name: "PHED", logo: "⚡", color: "bg-green-600" },
  ]

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  useEffect(() => {
    if (selectedProvider && activeTab === "cable") {
      loadCablePlans()
    }
  }, [selectedProvider, activeTab])

  const loadCablePlans = async () => {
    setLoading(true)
    try {
      const cablePlans = await getCablePlans(selectedProvider)
      setPlans(cablePlans)
    } catch (error) {
      toast.error("Failed to load plans")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" })
    }
  }

  const validateCableForm = () => {
    const newErrors = {}

    if (!selectedProvider) {
      newErrors.provider = "Please select a provider"
    }

    if (!formData.smartCardNumber) {
      newErrors.smartCardNumber = "Smart card number is required"
    }

    if (!selectedPlan) {
      newErrors.plan = "Please select a plan"
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required"
    } else if (!/^(\+234|0)[789][01]\d{8}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid Nigerian phone number"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateElectricityForm = () => {
    const newErrors = {}

    if (!selectedProvider) {
      newErrors.provider = "Please select a provider"
    }

    if (!formData.meterNumber) {
      newErrors.meterNumber = "Meter number is required"
    }

    if (!formData.amount) {
      newErrors.amount = "Amount is required"
    } else if (isNaN(formData.amount) || Number.parseFloat(formData.amount) < 500) {
      newErrors.amount = "Minimum amount is ₦500"
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required"
    } else if (!/^(\+234|0)[789][01]\d{8}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid Nigerian phone number"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePayment = () => {
    const isValid = activeTab === "cable" ? validateCableForm() : validateElectricityForm()
    if (!isValid) return

    const amount = activeTab === "cable" ? selectedPlan.price : Number.parseFloat(formData.amount)
    if (user.balance < amount) {
      toast.error("Insufficient wallet balance")
      return
    }

    setShowConfirmModal(true)
  }

  const confirmPayment = async () => {
    setPurchasing(true)
    setShowConfirmModal(false)

    try {
      let paymentResponse

      if (activeTab === "cable") {
        paymentResponse = await payCableBill({
          provider: selectedProvider,
          smartCardNumber: formData.smartCardNumber,
          planId: selectedPlan.id,
          planName: selectedPlan.name,
          amount: selectedPlan.price,
          phone: formData.phone,
        })
      } else {
        paymentResponse = await payElectricityBill({
          provider: selectedProvider,
          meterNumber: formData.meterNumber,
          amount: Number.parseFloat(formData.amount),
          phone: formData.phone,
        })
      }

      if (paymentResponse.code === "000") {
        const amount = activeTab === "cable" ? selectedPlan.price : Number.parseFloat(formData.amount)

        // Deduct from wallet
        deductBalance(amount)

        // Add transaction
        addTransaction({
          type: activeTab === "cable" ? "Cable TV Payment" : "Electricity Payment",
          provider: selectedProvider.toUpperCase(),
          phone: formData.phone,
          amount: amount,
          plan: activeTab === "cable" ? selectedPlan.name : `₦${amount} Units`,
          status: "completed",
          transactionId: paymentResponse.content.transactions.transactionId,
          reference: activeTab === "cable" ? formData.smartCardNumber : formData.meterNumber,
        })

        toast.success("Payment successful!")

        // Reset form
        setSelectedProvider("")
        setSelectedPlan(null)
        setPlans([])
        setFormData({
          smartCardNumber: "",
          meterNumber: "",
          customerName: "",
          phone: "",
          amount: "",
        })

        // Redirect to transactions
        setTimeout(() => {
          navigate("/transactions")
        }, 2000)
      } else {
        toast.error("Payment failed. Please try again.")
      }
    } catch (error) {
      toast.error("Payment failed. Please try again.")
    } finally {
      setPurchasing(false)
    }
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CreditCard className="mr-3 text-purple-600" />
            Pay Bills
          </h1>
          <p className="text-gray-600 mt-2">Pay for cable TV, electricity, and other utilities</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab("cable")
                  setSelectedProvider("")
                  setSelectedPlan(null)
                  setPlans([])
                  setFormData({ ...formData, smartCardNumber: "", meterNumber: "", amount: "" })
                  setErrors({})
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "cable"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Tv className="inline-block mr-2 h-5 w-5" />
                Cable TV
              </button>
              <button
                onClick={() => {
                  setActiveTab("electricity")
                  setSelectedProvider("")
                  setSelectedPlan(null)
                  setPlans([])
                  setFormData({ ...formData, smartCardNumber: "", meterNumber: "", amount: "" })
                  setErrors({})
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "electricity"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Zap className="inline-block mr-2 h-5 w-5" />
                Electricity
              </button>
            </nav>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl font-semibold mb-6">
                {activeTab === "cable" ? "Cable TV Payment" : "Electricity Payment"}
              </h2>

              {/* Provider Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose Provider</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(activeTab === "cable" ? cableProviders : electricityProviders).map((provider) => (
                    <div
                      key={provider.id}
                      onClick={() => {
                        setSelectedProvider(provider.id)
                        setSelectedPlan(null)
                        setErrors({})
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedProvider === provider.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="text-center">
                        <div
                          className={`w-12 h-12 ${provider.color} rounded-full flex items-center justify-center mx-auto mb-2`}
                        >
                          <span className="text-xl">{provider.logo}</span>
                        </div>
                        <p className="font-medium">{provider.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.provider && <p className="mt-1 text-sm text-red-600">{errors.provider}</p>}
              </div>

              {/* Cable TV Form */}
              {activeTab === "cable" && (
                <>
                  <Input
                    label="Smart Card Number"
                    type="text"
                    value={formData.smartCardNumber}
                    onChange={(e) => handleInputChange("smartCardNumber", e.target.value)}
                    error={errors.smartCardNumber}
                    placeholder="Enter your smart card number"
                  />

                  {selectedProvider && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Select Package</label>
                      {loading ? (
                        <div className="py-8">
                          <Loader />
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {plans.map((plan) => (
                            <div
                              key={plan.id}
                              onClick={() => {
                                setSelectedPlan(plan)
                                setErrors({})
                              }}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                selectedPlan?.id === plan.id
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-semibold">{plan.name}</p>
                                  <p className="text-sm text-gray-600">{plan.duration}</p>
                                </div>
                                <p className="font-bold text-lg">₦{plan.price.toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {errors.plan && <p className="mt-1 text-sm text-red-600">{errors.plan}</p>}
                    </div>
                  )}
                </>
              )}

              {/* Electricity Form */}
              {activeTab === "electricity" && (
                <>
                  <Input
                    label="Meter Number"
                    type="text"
                    value={formData.meterNumber}
                    onChange={(e) => handleInputChange("meterNumber", e.target.value)}
                    error={errors.meterNumber}
                    placeholder="Enter your meter number"
                  />

                  <Input
                    label="Amount (₦)"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleInputChange("amount", e.target.value)}
                    error={errors.amount}
                    placeholder="Enter amount"
                    min="500"
                  />
                </>
              )}

              <Input
                label="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                error={errors.phone}
                placeholder="e.g., 08123456789"
              />

              <Button onClick={handlePayment} disabled={purchasing} loading={purchasing} className="w-full">
                Pay Bill
              </Button>
            </Card>
          </div>

          {/* Summary Section */}
          <div>
            <Card>
              <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium">{activeTab === "cable" ? "Cable TV" : "Electricity"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Provider:</span>
                  <span className="font-medium">{selectedProvider ? selectedProvider.toUpperCase() : "-"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">{activeTab === "cable" ? "Smart Card:" : "Meter Number:"}</span>
                  <span className="font-medium">
                    {activeTab === "cable" ? formData.smartCardNumber || "-" : formData.meterNumber || "-"}
                  </span>
                </div>

                {activeTab === "cable" && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Package:</span>
                    <span className="font-medium">{selectedPlan ? selectedPlan.name : "-"}</span>
                  </div>
                )}

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-purple-600">
                    ₦
                    {activeTab === "cable"
                      ? selectedPlan
                        ? selectedPlan.price.toLocaleString()
                        : "0"
                      : formData.amount
                        ? Number.parseFloat(formData.amount).toLocaleString()
                        : "0"}
                  </span>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Wallet Balance:</span>
                    <span className="text-green-600">₦{user.balance?.toLocaleString() || "0"}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Payment">
          <div className="space-y-4">
            <p>Are you sure you want to make this payment?</p>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Service:</span>
                <span className="font-medium">{activeTab === "cable" ? "Cable TV" : "Electricity"}</span>
              </div>
              <div className="flex justify-between">
                <span>Provider:</span>
                <span className="font-medium">{selectedProvider?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span>{activeTab === "cable" ? "Smart Card:" : "Meter Number:"}</span>
                <span className="font-medium">
                  {activeTab === "cable" ? formData.smartCardNumber : formData.meterNumber}
                </span>
              </div>
              {activeTab === "cable" && (
                <div className="flex justify-between">
                  <span>Package:</span>
                  <span className="font-medium">{selectedPlan?.name}</span>
                </div>
              )}
              <div className="flex justify-between font-bold">
                <span>Amount:</span>
                <span className="text-purple-600">
                  ₦
                  {activeTab === "cable"
                    ? selectedPlan?.price.toLocaleString()
                    : Number.parseFloat(formData.amount || 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex space-x-4">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmPayment} loading={purchasing} className="flex-1">
                Confirm Payment
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default PayBills
